import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Search } from "lucide-react";
import { useCart } from "@/lib/cart";
import { api } from "@/lib/api";

const desktopLinks = [
  { to: "/", label: "HOME" },
  { to: "/shop", label: "SHOP" },
  { to: "/shop?new=1", label: "LATEST DROPS" },
  { to: "/categories", label: "CATEGORIES" },
  { to: "/coming-soon", label: "COMING SOON" },
  { to: "/about", label: "ABOUT" },
  { to: "/faq", label: "FAQ" },
];

export default function Layout({ children }) {
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  const loc = useLocation();

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const brand = settings.brand_name || "FERAL RIOT";

  return (
    <div className="grain min-h-screen flex flex-col">
      {/* Marquee */}
      <div className="border-b border-[color:var(--border)] py-2 text-[11px] tracking-widest uppercase overflow-hidden">
        <div className="marquee">
          <div className="marquee-track">
            {Array.from({ length: 8 }).map((_, i) => (
              <span key={i} className="opacity-70">GRATIS ONGKIR PEMBELIAN Rp500.000+ &nbsp;·&nbsp; MADE TO ORDER &nbsp;·&nbsp; LATEST DROP</span>
            ))}
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="border-b border-[color:var(--border)] bg-[color:var(--bg)] sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="display text-3xl md:text-4xl tracking-tight">{brand}</Link>
          <nav className="hidden lg:flex gap-8 text-xs tracking-widest">
            {desktopLinks.map((l) => (
              <NavLink key={l.to+l.label} to={l.to} data-testid={`nav-${l.label.replace(/\s+/g,'-').toLowerCase()}`}
                className={({ isActive }) => `hover:text-white transition-colors ${isActive ? "text-white" : "text-neutral-400"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/track" data-testid="nav-track" className="hidden md:block text-xs tracking-widest text-neutral-400 hover:text-white">TRACK</Link>
            <Link to="/cart" data-testid="nav-cart" className="relative flex items-center gap-1 text-xs tracking-widest">
              <ShoppingBag size={18} strokeWidth={1.25} />
              <span data-testid="cart-count" className="tabular-nums">({count})</span>
            </Link>
            <button data-testid="menu-toggle" aria-label="menu" className="lg:hidden" onClick={() => setOpen(true)}>
              <Menu size={22} strokeWidth={1.25} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {open && (
        <div className="fixed inset-0 z-50 bg-[color:var(--bg)] flex flex-col">
          <div className="flex items-center justify-between px-6 py-5 border-b border-[color:var(--border)]">
            <span className="display text-3xl">{brand}</span>
            <button data-testid="menu-close" onClick={() => setOpen(false)}><X size={22} /></button>
          </div>
          <nav className="flex flex-col gap-2 p-6">
            {["/", "/shop", "/shop?new=1", "/coming-soon", "/categories", "/about", "/faq", "/track", "/cart"].map((to, i) => {
              const label = ["HOME","SHOP","LATEST","COMING SOON","CATEGORIES","ABOUT","FAQ","TRACK ORDER","CART"][i];
              return <Link key={to+label} to={to} data-testid={`m-nav-${label.replace(/\s+/g,'-').toLowerCase()}`} className="display text-5xl py-2 border-b border-[color:var(--border)]">{label}</Link>;
            })}
          </nav>
        </div>
      )}

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[color:var(--border)] mt-24">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10 text-sm">
          <div>
            <div className="display text-3xl mb-3">{brand}</div>
            <p className="text-neutral-400 text-xs leading-relaxed">{settings.tagline || "Independent Streetwear Label"}</p>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-neutral-500 mb-3">SHOP</div>
            <ul className="space-y-2 text-neutral-300 text-xs">
              <li><Link to="/shop">Semua Produk</Link></li>
              <li><Link to="/shop?new=1">Latest Drops</Link></li>
              <li><Link to="/coming-soon">Coming Soon</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-neutral-500 mb-3">BANTUAN</div>
            <ul className="space-y-2 text-neutral-300 text-xs">
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/track">Lacak Pesanan</Link></li>
              <li><Link to="/contact">Hubungi Kami</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-widest text-neutral-500 mb-3">KONTAK</div>
            <ul className="space-y-2 text-neutral-300 text-xs">
              <li>WhatsApp: {settings.whatsapp_number || "-"}</li>
              <li>IG: {settings.instagram || "-"}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[color:var(--border)] px-4 sm:px-8 py-6 text-[10px] tracking-widest flex justify-between text-neutral-500">
          <span>© {new Date().getFullYear()} {brand}</span>
          <Link to="/admin/login" data-testid="admin-link">ADMIN</Link>
        </div>
      </footer>
    </div>
  );
}
